import { useEffect, useMemo, useState } from 'react';

import styled from '@emotion/styled';

import { useQuery } from '@tanstack/react-query';

import { getBillsByVendor } from '../../Bills/apiService';

import { getAppliedPayments } from '../apiService';

import { Grid, Checkbox, TextField, InputAdornment } from '@mui/material';

import { useLocation } from 'react-router-dom';

import { useFormikContext } from 'formik';

import { useFormData } from '../../../contexts/FormData';

import { BasicFlex } from '../../../utils/Common';

import NoItems from '../../../utils/NoItems';

import { formatGenericDate, formatCurrency, fxn } from '../../../utils/util';

import PaidRoundedIcon from '@mui/icons-material/PaidRounded';

import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';

import SimpleChip from '../../../utils/utilities/SimpleChip';

import { truncate } from 'lodash';

import useIsMobile from '../../../hooks/useIsMobile';

/* ------------------------------------------------------------------ */
/*  local design tokens (swap these for theme tokens if you have them) */
/* ------------------------------------------------------------------ */
const ACCENT = '#2f6fed';
const ACCENT_SOFT = '#eaf1fe';
const DANGER = '#c2410c';
const DANGER_BG = '#fff1e9';
const WARN = '#92710a';
const WARN_BG = '#fcf6e3';
const PAID = '#15803d';
const PAID_BG = '#e7f6ec';

/* ------------------------------------------------------------------ */
/*  styled components                                                  */
/* ------------------------------------------------------------------ */
/* NOTE: this app uses a 1rem = 10px root (font-size: 62.5%), so all rem values
   below are sized for that base (e.g. 1.4rem = 14px). */

const CardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.6rem;

  @media ${p => p.theme.breaks.down('md')} {
    gap: 1.2rem;
  }
`;

const Card = styled.div`
  background: #fff;
  border: 1px solid ${p => (p.$sel ? '#bcd3fb' : p.theme.colors.border)};
  border-radius: 14px;
  overflow: hidden;
  box-shadow: ${p => (p.$sel ? `0 0 0 1px #bcd3fb, 0 1px 2px rgba(20,30,50,.04)` : '0 1px 2px rgba(20,30,50,.04)')};
`;

const Header = styled.div`
  display: grid;
  grid-template-columns: auto 1fr auto auto;
  grid-template-areas: 'check info amt chev';
  align-items: center;
  gap: 1.6rem;
  padding: .3rem 1rem;
  padding-top: 1rem;
  border-left: 4px solid ${p => (p.$paid ? PAID : ACCENT)};
  background: linear-gradient(180deg, #fbfdff 0%, #ffffff 100%);

  @media ${p => p.theme.breaks.down('md')} {
    grid-template-columns: auto 1fr auto;
    grid-template-areas:
      'check info chev'
      'amt   amt  amt';
    column-gap: 1.2rem;
    row-gap: 1rem;
  }
`;

const Info = styled.div`
  grid-area: info;
  min-width: 0;
  cursor: pointer;
`;

const TopRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
`;

const BillNo = styled.div`
  font-size: 1.7rem;
  font-weight: 700;
  letter-spacing: -0.01em;
`;

const CountChip = styled.span`
  font-size: 1.15rem;
  font-weight: 600;
  color: ${p => p.theme.colors.lightText};
  background: #eef2f7;
  padding: 0.2rem 0.9rem;
  border-radius: 20px;
`;

const Pill = styled.span`
  font-size: 1.1rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  padding: 0.25rem 0.9rem;
  border-radius: 20px;
  color: ${p => (p.$tone === 'due' ? DANGER : p.$tone === 'today' ? WARN : p.theme.colors.lightText)};
  background: ${p => (p.$tone === 'due' ? DANGER_BG : p.$tone === 'today' ? WARN_BG : 'transparent')};
`;

const PaidTag = styled.span`
  font-size: 1.1rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  padding: 0.25rem 0.9rem;
  border-radius: 20px;
  color: ${PAID};
  background: ${PAID_BG};
`;

const Meta = styled.div`
  margin-top: 0.5rem;
  font-size: 1.4rem;
  color: ${p => p.theme.colors.lightText};
`;

const AmtCell = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.4rem;
  min-width: 17rem;

  @media ${p => p.theme.breaks.down('md')} {
    min-width: 0;
  }
`;

const FieldLabel = styled.div`
  font-size: 1.1rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${p => p.theme.colors.lightText};
`;

const Ref = styled.div`
  font-size: 1.2rem;
  font-weight: ${p => (p.$paid ? 600 : 400)};
  color: ${p => (p.$paid ? PAID : p.theme.colors.lightText)};
`;

const Chev = styled.button`
  grid-area: chev;
  border: none;
  background: transparent;
  cursor: pointer;
  color: ${p => p.theme.colors.lightText};
  display: grid;
  place-items: center;
  padding: 0.4rem;
  svg {
    font-size: 2.4rem;
    transition: transform 0.18s ease;
    transform: rotate(${p => (p.$open ? 180 : 0)}deg);
  }
`;

const WigList = styled.div`
  background: #f8fafc;
  border-top: 1px solid ${p => p.theme.colors.border};
  padding: 0.4rem 2rem 1.4rem 1rem;

  @media ${p => p.theme.breaks.down('md')} {
    padding: 0.4rem 1rem 1.2rem 0.6rem;
  }
`;

const WigHead = styled.div`
  font-size: 1.1rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${p => p.theme.colors.lightText};
  padding: 1rem 0 0.4rem 3.2rem;

  @media ${p => p.theme.breaks.down('md')} {
    padding-left: 2.4rem;
  }
`;

const WigRow = styled.div`
  display: grid;
  grid-template-columns: auto 1fr auto;
  grid-template-areas: 'check content amt';
  align-items: center;
  gap: 1.6rem;
  padding: 1.1rem 0 1.1rem 3.2rem;
  position: relative;
  opacity: ${p => (p.$paid ? 0.62 : 1)};
  border-bottom: 1px solid ${p => p.theme.colors.border};
  &:last-of-type {
    border-bottom: none;
  }
  &::before {
    content: '';
    position: absolute;
    left: 1rem;
    top: 50%;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${p => (p.$paid ? PAID : p.$on ? ACCENT : '#cdd6e3')};
    transform: translateY(-50%);
  }

  @media ${p => p.theme.breaks.down('md')} {
    grid-template-columns: auto 1fr;
    grid-template-areas:
      'check content'
      'check amt';
    align-items: start;
    column-gap: 1.2rem;
    row-gap: 0.8rem;
    padding-left: 2.4rem;

    &::before {
      top: 1.9rem;
    }
  }
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
`;

const WigTitle = styled.div`
  font-weight: 600;
  font-size: 1.4rem;
`;

const Sku = styled.div`
  font-family: 'Roboto Mono', 'SF Mono', Menlo, Consolas, monospace;
  font-size: 1.1rem;
  font-weight: bold;
  text-transform: uppercase;
  line-height: 1.3;
  color: rgba(0, 0, 0, 0.5);
  /* margin-top: 3px; */
`;

const PriceLine = styled.div`
  margin-top: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.8rem;
  font-size: 1.4rem;
  color: ${p => p.theme.colors.lightText};
`;

const OriginalAmount = styled.span`
  text-decoration: line-through;
  color: ${p => p.theme.colors.lightText};
`;

const SummaryBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 3.2rem;
  flex-wrap: wrap;
  margin-top: 1.6rem;
  padding: 1.4rem 1.8rem;
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: 12px;
  background: #fff;

  @media ${p => p.theme.breaks.down('md')} {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1.4rem 1.6rem;
    padding: 1.4rem;
  }
`;

const SummaryItem = styled.div`
  .label {
    font-size: 1.1rem;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: ${p => p.theme.colors.lightText};
  }
  .num {
    font-size: 1.7rem;
    font-weight: 700;
    margin-top: 0.2rem;
    color: ${p => (p.$accent ? ACCENT : 'inherit')};
  }
`;

const Hint = styled.div`
  font-size: 1.4rem;
  color: ${WARN};
  background: ${WARN_BG};
  padding: 0.8rem 1.4rem;
  border-radius: 8px;
  margin-top: 1.6rem;
`;

/* full-width "reveal the rest" control shown when a bill/wig focus is active */
const RevealBtn = styled.button`
  width: 100%;
  margin-top: 1.6rem;
  border: 1px dashed ${p => p.theme.colors.border};
  background: #fff;
  color: ${ACCENT};
  font-size: 1.4rem;
  font-weight: 600;
  padding: 1.2rem;
  border-radius: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  transition: background 0.15s ease, border-color 0.15s ease;

  svg {
    font-size: 2rem;
    transition: transform 0.18s ease;
    transform: rotate(${p => (p.$open ? 180 : 0)}deg);
  }

  &:hover {
    background: ${ACCENT_SOFT};
    border-color: #bcd3fb;
  }
`;

/* ------------------------------------------------------------------ */
/*  shared money input                                                 */
/* ------------------------------------------------------------------ */
const MoneyField = ({ value, onChange, disabled, big }) => (
  <TextField
    size="small"
    type="number"
    value={value}
    onChange={onChange}
    disabled={disabled}
    placeholder="0.00"
    onFocus={e => e.target.select()}
    slotProps={{
      input: {
        startAdornment: <InputAdornment position="start">$</InputAdornment>,
        style: { backgroundColor: '#fff' },
      },
    }}
    sx={{
      width: '12rem',
      '& input': { textAlign: 'right', fontWeight: 600, fontSize: big ? '1.7rem' : '1.4rem' },
      '& .MuiInputAdornment-root .MuiTypography-root': { fontSize: big ? '1.7rem' : '1.4rem' },
    }}
  />
);

/* ------------------------------------------------------------------ */
/*  helpers                                                            */
/* ------------------------------------------------------------------ */

// group the flat applied_payments array by bill while preserving each row's
// ORIGINAL flat index, because the change handlers operate on that index.
const buildGroups = (rows = []) => {
  const groups = [];
  let current = null;
  rows.forEach((row, i) => {
    if (!row.title) {
      // parent bill row
      current = { billId: row.bill_id, parent: { row, i }, children: [] };
      groups.push(current);
    } else if (current && current.billId === row.bill_id) {
      current.children.push({ row, i });
    } else {
      // orphan child (shouldn't normally happen) – give it its own group
      current = { billId: row.bill_id, parent: null, children: [{ row, i }] };
      groups.push(current);
    }
  });
  return groups;
};

const num = v => Number(v) || 0;

// a line item is fully settled when nothing remains on it
const isPaid = row => num(row?.balance) <= 0;

// total of all SELECTED line items (titled rows only), optionally excluding
// some indices (the row[s] about to be rewritten). This is position-independent,
// so the global cap holds no matter what order rows are edited in. Parents are
// never counted because the backend only persists titled rows.
const selectedChildTotal = (rows, exclude) =>
  rows.reduce((sum, r, idx) => {
    if (exclude?.has(idx)) return sum;
    return r?.selected && r?.title ? sum + num(r.amount) : sum;
  }, 0);

// flat indices of a bill's child line items (the rows the waterfall touches).
const childIndicesOf = (rows, parentIndex, billId) => {
  const idxs = [];
  for (let c = parentIndex + 1; c < rows.length && rows[c].title && rows[c].bill_id === billId; c++) {
    idxs.push(c);
  }
  return idxs;
};

// trim selected line items down so their total fits `budget`, keeping earlier
// rows intact and dropping the overflow off the end (same order as the waterfall).
// Returns the original array reference when nothing needed trimming.
const clampToBudget = (rows, budget) => {
  let remaining = Math.max(0, budget);
  let changed = false;
  const next = rows.map(r => {
    if (!(r?.selected && r?.title)) return r;
    const cur = num(r.amount);
    const give = fxn(Math.min(cur, remaining));
    remaining -= give;
    if (give === cur) return r;
    changed = true;
    return give > 0 ? { ...r, amount: give } : { ...r, amount: '', selected: false };
  });
  return changed ? next : rows;
};

// spread `total` across a bill's children, filling each to its balance in order.
// Fully-paid children (balance 0) naturally receive nothing.
const distributeAcrossChildren = (rows, parentIndex, billId, total) => {
  let remaining = total;
  for (let c = parentIndex + 1; c < rows.length && rows[c].title && rows[c].bill_id === billId; c++) {
    const give = fxn(Math.min(remaining, rows[c].balance));
    remaining -= give;
    rows[c] = { ...rows[c], amount: give > 0 ? give : '', selected: give > 0 };
  }
};

const dueTone = due_date => {
  if (!due_date) return null;
  const d = new Date(due_date);
  const now = new Date();
  d.setHours(0, 0, 0, 0);
  now.setHours(0, 0, 0, 0);
  if (d < now) return 'due';
  if (d.getTime() === now.getTime()) return 'today';
  return null;
};

// Stable identity for a line across the "fresh open" list and the "applied payment"
// list, so we can merge them per-line instead of per-bill.
const lineKey = row => {
  if (!row.title) return `${row.bill_id}::bill`;                 // parent bill header
  if (row.inventory_id) return `${row.bill_id}::inv::${row.inventory_id}`; // a specific wig
  if (row.wig_id) return `${row.bill_id}::wig::${row.wig_id}`;   // wig without inventory id
  return `${row.bill_id}::rest`;                                 // "Rest of Bill"
};

// Reduce the full open-bills list to the focus target coming from location.state.
//   • bill_id only      → just that bill (parent + all its lines).
//   • inventory_id      → that bill's header (rebalanced so the bill total reads
//                         only the focused wig) plus ONLY the focused wig.
// If inventory_id arrives without a bill_id we resolve the bill from the wig row.
// Falls back to the full list (or the whole bill) when the target can't be found,
// so navigation with a stale id never renders an empty screen.
const filterToFocus = (rows, focusBillId, focusInvId) => {
  if (!rows?.length) return rows;

  let billId = focusBillId;
  if (!billId && focusInvId) {
    billId = rows.find(r => r.inventory_id === focusInvId)?.bill_id ?? null;
  }
  if (!billId) return rows; // couldn't resolve a target → show everything

  const billRows = rows.filter(r => r.bill_id?.toString() === billId?.toString());
  if (!billRows.length) return rows; // target bill not in the open list → show everything
  if (!focusInvId) return billRows;  // bill-level focus → the whole bill

  const parent = billRows.find(r => !r.title);
  const wig = billRows.find(r => r.inventory_id === focusInvId);
  if (!wig) return billRows; // wig not open (e.g. already paid) → fall back to the whole bill

  // header/bill total should reflect ONLY the focused wig's numbers
  const scopedParent = parent ? { ...parent, total: wig.total, balance: wig.balance } : null;
  return scopedParent ? [scopedParent, wig] : [wig];
};

// Order the flat list by bill due date (asc) WITHOUT breaking grouping: segment
// into bill groups (parent + its children), sort the groups, then flatten. Rows
// with no due date sort last; equal due dates keep their original order (stable).
const sortGroupsByDueDate = rows => {
  if (!rows?.length) return rows;
  const groups = [];
  let current = null;
  for (const row of rows) {
    if (!row.title || !current) {
      current = { due: row.due_date, rows: [row] };
      groups.push(current);
    } else {
      current.rows.push(row);
    }
  }
  groups.sort((a, b) => {
    const da = a.due ? new Date(a.due).getTime() : Infinity;
    const db = b.due ? new Date(b.due).getTime() : Infinity;
    return da - db;
  });
  return groups.flatMap(g => g.rows);
};

const getPaymentList = async (vendor_id, payment_id, bill_id) => {
  try {
    let bills = await getBillsByVendor(vendor_id, 'open', bill_id);
    let bill_lines = [];

    // -------- Phase 1: every open bill + its wigs (+ remainder), all blank --------
    if (bills.length) {
      for (let bill of bills) {
        let rest_payments = bill.applied_payments.filter(bpa => !bpa.inventory_id)?.reduce((a, b) => a + b.amount, 0) || 0;
        bill_lines.push({
          bill_id: bill.bill_id,
          bill_num: bill.bill_num,
          due_date: bill.due_date,
          total: bill.total,
          balance: bill.balance,
          amount: '',
          selected: false,
        });
        if (bill.wigs?.length) {
          let wigs_total = bill.wigs.reduce((a, b) => a + b.prorated_amount, 0);
          let rest_of_bill = bill.total - wigs_total;
          for (let wig of bill.wigs) {
            let wig_paid = bill.applied_payments.filter(bpa => bpa.inventory_id === wig.inventory_id)?.reduce((a, b) => a + b.amount, 0) || 0;
            bill_lines.push({
              bill_id: bill.bill_id,
              bill_num: bill.bill_num,
              title: wig.wig_id,
              description: wig.description,
              wig_id: wig.wig_id,
              inventory_id: wig.inventory_id,
              due_date: bill.due_date,
              calculated_amount: wig.calculated_amount,
              total: wig.prorated_amount,
              balance: wig.prorated_amount - wig_paid,
              amount: '',
              selected: false,
            });
          }
          if (rest_of_bill > 0) {
            bill_lines.push({
              bill_id: bill.bill_id,
              bill_num: bill.bill_num,
              title: 'Rest of Bill',
              due_date: bill.due_date,
              total: rest_of_bill,
              balance: rest_of_bill - rest_payments,
              amount: '',
              selected: false,
            });
          }
        } else {
          // No wigs: the whole bill is one payable line. The header checkbox/amount
          // needs a titled row to drive, and the backend only persists titled rows,
          // so emit a single "Rest of Bill" line for the full balance.
          bill_lines.push({
            bill_id: bill.bill_id,
            bill_num: bill.bill_num,
            title: 'Rest of Bill',
            due_date: bill.due_date,
            total: bill.total,
            balance: bill.balance,
            amount: '',
            selected: false,
          });
        }
      }
    }

    // -------- Phase 2: overlay an existing payment (edit mode) --------
    if (payment_id) {
      let past_payments = await getAppliedPayments(payment_id);
      if (past_payments?.length) {
        // Rebuild parent + child rows for each bill this payment touched, with the
        // applied amounts prefilled and the balance restored (add back this payment).
        const ap = [];
        const chunkByPayment = {};
        for (const p of past_payments) {
          (chunkByPayment[p.bill_id] ||= []).push(p);
        }
        for (const p of Object.values(chunkByPayment)) {
          const bi = p[0];
          const total_amount = p.reduce((a, b) => a + b.amount, 0);
          ap.push({
            vendor_payment_id: bi.vendor_payment_id,
            bill_id: bi.bill_id,
            bill_num: bi.bill_num,
            due_date: bi.due_date,
            total: bi.total,
            balance: +bi.balance + +total_amount,
            amount: total_amount,
            selected: true,
          });
          p.forEach(pi => {
            ap.push({
              vendor_payment_id: pi.vendor_payment_id,
              bill_id: pi.bill_id,
              bill_num: pi.bill_num,
              due_date: pi.due_date,
              title: pi.inventory_id ? pi.wig_id : 'Rest of Bill',
              inventory_id: pi.inventory_id,
              wig_id: pi.wig_id,
              description: pi.description,
              total: pi.item_total,
              balance: +pi.balance + +pi.amount,
              amount: pi.amount,
              selected: true,
            });
          });
        }

        // Index the applied rows by line key.
        const appliedByKey = new Map(ap.map(r => [lineKey(r), r]));

        // 1) Overlay applied data onto the matching fresh rows. Unmatched fresh rows
        //    (the still-open wigs on a partially-paid bill) are left untouched/blank.
        const consumed = new Set();
        bill_lines = bill_lines.map(row => {
          const hit = appliedByKey.get(lineKey(row));
          if (!hit) return row;
          consumed.add(lineKey(row));
          return {
            ...row,
            vendor_payment_id: hit.vendor_payment_id,
            balance: hit.balance, // restored (pre-this-payment) balance
            amount: hit.amount,
            selected: true,
          };
        });

        // 2) Any applied rows with no fresh match = wigs already fully paid and no
        //    longer returned as "open". Splice them back into their bill group so
        //    each bill's rows stay contiguous (parent first, children after).
        const leftovers = ap.filter(r => !consumed.has(lineKey(r)));
        if (leftovers.length) {
          const leftoverByBill = {};
          for (const r of leftovers) (leftoverByBill[r.bill_id] ||= []).push(r);

          const merged = [];
          const handled = new Set();
          for (let i = 0; i < bill_lines.length; i++) {
            merged.push(bill_lines[i]);
            const next = bill_lines[i + 1];
            const endOfBill = !next || next.bill_id !== bill_lines[i].bill_id;
            if (endOfBill && leftoverByBill[bill_lines[i].bill_id]) {
              merged.push(...leftoverByBill[bill_lines[i].bill_id]);
              handled.add(bill_lines[i].bill_id);
            }
          }
          // Bills that had NO fresh rows at all (fully paid, not in the open list):
          // append their whole group (parent + children).
          for (const [bill, rows] of Object.entries(leftoverByBill)) {
            if (!handled.has(bill)) merged.push(...rows);
          }
          bill_lines = merged;
        }
      }
    }

    return sortGroupsByDueDate(bill_lines);
  } catch (e) {
    return null;
  }
};

/* ------------------------------------------------------------------ */
/*  one invoice group                                                  */
/* ------------------------------------------------------------------ */
const InvoiceGroup = ({ group, expanded, onToggle, onChangeAmount, onChangeSelect, isNotDesktop }) => {
  const { parent, children } = group;
  const head = parent?.row || children[0]?.row;
  const hasChildren = children.length > 0;

  // only line items that still have a balance can be acted on
  const payable = children.filter(c => !isPaid(c.row));
  const billPayable = payable.length > 0;
  const billPaid = hasChildren && !billPayable;
  const lockHeader = !hasChildren || !parent || !billPayable;

  const selCount = payable.filter(c => c.row.selected).length;
  const allSelected = billPayable && selCount === payable.length;
  const someSelected = selCount > 0 && selCount < payable.length;
  const payingSum = children.reduce((a, c) => a + num(c.row.amount), 0);

  const wigCount = children.filter(c => c.row.wig_id).length;
  const allWigs = hasChildren && wigCount === children.length;
  const countLabel = `${children.length} ${allWigs ? 'wig' : 'item'}${children.length !== 1 ? 's' : ''}`;

  const tone = dueTone(head?.due_date);

  return (
    <Card $sel={payingSum > 0}>
      <Header $paid={billPaid}>
        <Checkbox
          size="small"
          checkedIcon={<PaidRoundedIcon />}
          checked={allSelected}
          indeterminate={someSelected}
          disabled={lockHeader}
          onChange={e => onChangeSelect(e, parent.i)}
          sx={{ gridArea: 'check' }}
        />

        <Info onClick={onToggle}>
          <TopRow>
            <BillNo>#{head?.bill_num}</BillNo>
            {hasChildren ? <CountChip>{countLabel}</CountChip> : null}
            {billPaid ? <PaidTag>Paid</PaidTag> : tone ? <Pill $tone={tone}>{tone === 'due' ? 'Past due' : 'Due today'}</Pill> : null}
          </TopRow>
          <Meta>Due {formatGenericDate(head?.due_date, 'short')}</Meta>
        </Info>

        <AmtCell style={{ gridArea: 'amt' }}>
          {/* <FieldLabel>Paying</FieldLabel> */}
          <MoneyField
            big={false}
            // big={true}
            value={payingSum > 0 ? payingSum : ''}
            disabled={lockHeader}
            onChange={e => onChangeAmount(e, parent.i)}
          />
          <Ref $paid={billPaid}>{billPaid ? 'Paid in full' : `${formatCurrency(head?.balance)} Bal`}</Ref>
        </AmtCell>

        <Chev $open={expanded} onClick={onToggle} aria-label="Toggle wigs" type="button">
          <KeyboardArrowDownRoundedIcon />
        </Chev>
      </Header>

      {expanded && hasChildren ? (
        <WigList>
          <WigHead>{allWigs ? 'Wigs on this bill' : 'Items on this bill'}</WigHead>
          {children.map(({ row, i }) => {
            const paid = isPaid(row);
            return (
              <WigRow key={`${row.bill_id}-${row.title}-${i}`} $on={!!row.selected} $paid={paid}>
                <Checkbox
                  size="small"
                  checkedIcon={<PaidRoundedIcon />}
                  checked={!!row.selected}
                  disabled={paid}
                  onChange={e => onChangeSelect(e, i)}
                  sx={{ gridArea: 'check' }}
                />

                <div style={{ gridArea: 'content', minWidth: 0 }}>
                  <TitleRow>
                    <WigTitle>{row.title}</WigTitle>
                    {paid ? <PaidTag>Paid</PaidTag> : null}
                  </TitleRow>
                  {row.description ? (
                    <BasicFlex gap=".8rem" style={{ marginTop: '.5rem' }}>
                      <Sku>{row.description}</Sku>
                    </BasicFlex>
                  ) : null}
                  <PriceLine>
                    {row.total < row.calculated_amount ? (
                      <OriginalAmount>{formatCurrency(row.calculated_amount)}</OriginalAmount>
                    ) : null}
                    <span>{formatCurrency(row.total)}</span>
                  </PriceLine>
                </div>

                <AmtCell style={{ gridArea: 'amt', minWidth: '15rem' }}>
                  <MoneyField value={row.amount ?? ''} disabled={paid} onChange={e => onChangeAmount(e, i)} />
                  <Ref $paid={paid}>{paid ? 'Paid in full' : `${formatCurrency(row.balance)} Bal`}</Ref>
                </AmtCell>
              </WigRow>
            );
          })}
        </WigList>
      ) : null}
    </Card>
  );
};

/* ------------------------------------------------------------------ */
/*  main component                                                     */
/* ------------------------------------------------------------------ */
const OpenBills = () => {
  const location = useLocation();

  const state = location.state;

  const { values } = useFormikContext() || {};
  const { formValues, setFormValues } = useFormData() || {};
  const { isNotDesktop } = useIsMobile();

  const payment_amount = values.total_amount ?? values.amount ?? 0;

  // Focus target passed via navigation state. Only applies to NEW payments —
  // edit mode rebuilds the whole payment from what was saved, so we never scope it.
  const isEdit = !!values?.vendor_payment_id;
  const focusBillId = !isEdit ? state?.bill_id || null : null;
  const focusInvId = !isEdit ? state?.inventory_id || null : null;
  const hasFocus = !!(focusBillId || focusInvId);

  // Display labels passed alongside the focus ids (wig_id with inventory_id,
  // bill_num with bill_id). Used only for the collapse button text; the generic
  // wording is the fallback when they aren't provided.
  const focusWigId = !isEdit ? state?.wig_id || null : null;
  const focusBillNum = !isEdit ? state?.bill_num || null : null;

  // While focused we hide the other bills (and, for a wig focus, the focused
  // bill's other wigs). Hidden rows are PHYSICALLY ABSENT from applied_payments,
  // so the waterfall / budget math literally can't reach them — only the focused
  // bill can consume the payment amount until the user reveals everything.
  const [showAll, setShowAll] = useState(false);

  const { data: bills } = useQuery({
    queryKey: ['bills', 'vendor', values?.vendor_id, values?.vendor_payment_id, values?.bill_id],
    queryFn: () => getPaymentList(values?.vendor_id, values?.vendor_payment_id, values?.bill_id),
    enabled: !!values?.vendor_id,
  });

  // Seed applied_payments from the query. On first load (and any refetch) we only
  // hand the handlers the focused subset when a focus is active and not yet
  // expanded; otherwise the full list. Reveal is handled separately so it never
  // clobbers amounts the user already typed on the focused rows.
  useEffect(() => {
    setFormValues(ofv => {
      if (!bills) return { ...ofv, applied_payments: bills };
      const view = hasFocus && !showAll ? filterToFocus(bills, focusBillId, focusInvId) : bills;
      return { ...ofv, applied_payments: view };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bills]);

  // Reveal the previously-hidden bills/wigs. Merge the full list back in, carrying
  // over amount/selected for any line the user already edited. Parent rows always
  // take their fresh (un-scoped) balance from the full list, so the wig-focus
  // header stops showing the single-wig balance once everything is on screen.
  const revealAllBills = () => {
    setShowAll(true);
    setFormValues(ofv => {
      const full = bills || [];
      const edits = new Map((ofv?.applied_payments || []).map(r => [lineKey(r), r]));
      const merged = full.map(row => {
        if (!row.title) return row; // parents: real balance from the fresh list
        const e = edits.get(lineKey(row));
        return e ? { ...row, amount: e.amount, selected: e.selected } : row;
      });
      return { ...ofv, applied_payments: merged };
    });
  };

  // Collapse back to the focused view and WIPE all applied money clean — start
  // fresh on just the focused bill/wig. Re-deriving from the pristine query data
  // (`bills`, never mutated) blanks every amount for free. Focus-only, so this is
  // never reachable in edit mode.
  const collapseToFocus = () => {
    setShowAll(false);
    setFormValues(ofv => ({
      ...ofv,
      applied_payments: bills ? filterToFocus(bills, focusBillId, focusInvId) : bills,
    }));
  };

  // When the payment amount changes (e.g. lowered), re-run the spread so the
  // applied total never exceeds it. Uses the functional updater so it reads the
  // latest rows and only depends on payment_amount (no update loop). Because it
  // operates on applied_payments, it only ever touches the currently-visible
  // (focused or revealed) rows.
  useEffect(() => {
    setFormValues(ofv => {
      const rows = ofv?.applied_payments;
      if (!rows?.length) return ofv;
      const applied = rows.filter(r => r.selected && r.title).reduce((a, b) => a + num(b.amount), 0);
      if (applied <= payment_amount) return ofv; // still within budget, nothing to trim
      return { ...ofv, applied_payments: clampToBudget(rows, payment_amount) };
    });
  }, [payment_amount]);

  const applied_payments = formValues?.applied_payments || [];

  const groups = useMemo(() => buildGroups(applied_payments), [applied_payments]);

  // What the focused view would show (independent of the current showAll state),
  // so the toggle button + labels stay stable whether we're expanded or collapsed.
  const focusedView = useMemo(
    () => (hasFocus && bills ? filterToFocus(bills, focusBillId, focusInvId) : bills),
    [hasFocus, bills, focusBillId, focusInvId],
  );

  // Show the toggle only when focus mode actually hides something (other bills
  // and/or the focused bill's other wigs). The focused view is a strict subset,
  // so a length gap means there's more to reveal.
  const focusHasMore = hasFocus && !!bills && !!focusedView && bills.length > focusedView.length;

  // Count of OTHER bills hidden by the focus, for a friendlier expand label.
  const otherBillCount = useMemo(() => {
    if (!bills?.length || !focusedView) return 0;
    const shownIds = new Set(focusedView.map(r => r.bill_id));
    return new Set(bills.filter(r => !shownIds.has(r.bill_id)).map(r => r.bill_id)).size;
  }, [bills, focusedView]);

  const expandLabel =
    otherBillCount > 0
      ? `Show ${otherBillCount} other bill${otherBillCount !== 1 ? 's' : ''}`
      : 'Show all items on this bill';
  const collapseLabel = focusInvId
    ? focusWigId
      ? `Show only wig ${focusWigId}`
      : 'Show only this item'
    : focusBillNum
    ? `Show only bill #${focusBillNum}`
    : 'Hide other bills';

  // expand override per bill. Default: collapsed when a bill has <= 1 item.
  const [openOverride, setOpenOverride] = useState({});
  const isExpanded = g => openOverride[g.billId] ?? g.children.length >= 2;
  const toggle = g => setOpenOverride(m => ({ ...m, [g.billId]: !isExpanded(g) }));

  /* ----------------------- change handlers ----------------------- */
  const handleChangeAmount = (e, index) => {
    const raw = e.target.value;
    setFormValues(ofv => {
      const next = ofv.applied_payments.map(el => ({ ...el }));
      const row = next[index];

      if (!row.title) {
        // parent bill: cap = payment left after EVERY other bill's wigs,
        // then waterfall that capped total down across this bill's wigs.
        const exclude = new Set(childIndicesOf(next, index, row.bill_id));
        const remaining = Math.max(0, payment_amount - selectedChildTotal(next, exclude));
        const total = raw === '' ? 0 : Math.max(0, Math.min(num(raw), row.balance, remaining));
        next[index] = { ...row, amount: total > 0 ? total : '', selected: total > 0 };
        distributeAcrossChildren(next, index, row.bill_id, total);
      } else {
        // single wig: cap = payment left after every OTHER selected wig
        const remaining = Math.max(0, payment_amount - selectedChildTotal(next, new Set([index])));
        const amount = raw === '' ? '' : Math.max(0, Math.min(num(raw), row.balance, remaining));
        next[index] = { ...row, amount, selected: !amount ? false : true };
      }
      return { ...ofv, applied_payments: next };
    });
  };

  const handleChangeSelect = (e, index) => {
    if (payment_amount <= 0) return;
    const checked = e.target.checked;
    setFormValues(ofv => {
      const next = ofv.applied_payments.map(el => ({ ...el }));
      const row = next[index];

      if (!row.title) {
        // parent: select fills all wigs (capped by remaining payment), deselect clears
        if (checked) {
          const exclude = new Set(childIndicesOf(next, index, row.bill_id));
          const remaining = Math.max(0, payment_amount - selectedChildTotal(next, exclude));
          const fill = Math.max(0, Math.min(row.balance, remaining));
          next[index] = { ...row, selected: fill > 0, amount: fill > 0 ? fill : '' };
          distributeAcrossChildren(next, index, row.bill_id, fill);
        } else {
          next[index] = { ...row, selected: false, amount: '' };
          for (const c of childIndicesOf(next, index, row.bill_id)) {
            next[c] = { ...next[c], selected: false, amount: '' };
          }
        }
      } else {
        // single wig
        if (checked) {
          const remaining = Math.max(0, payment_amount - selectedChildTotal(next, new Set([index])));
          const amt = Math.max(0, Math.min(row.balance, remaining));
          next[index] = { ...row, selected: amt > 0, amount: amt > 0 ? amt : '' };
        } else {
          next[index] = { ...row, selected: false, amount: '' };
        }
      }
      return { ...ofv, applied_payments: next };
    });
  };

  /* --------------------------- totals ---------------------------- */
  const appliedTotal = applied_payments
    .filter(r => r.selected && r.title)
    .reduce((a, b) => a + num(b.amount), 0);
  const openTotal = applied_payments.filter(r => !r.title).reduce((a, b) => a + num(b.balance), 0);
  const remainingBudget = payment_amount - appliedTotal;

  return (
    <Grid
      size={{ xs: 12 }}
      sx={{ backgroundColor: bills?.length ? 'bg.light' : 'transparent', padding: '1.6rem', borderRadius: '0.8rem' }}
    >
      {applied_payments?.length ? (
        <>
          <CardList>
            {groups.map(g => (
              <InvoiceGroup
                key={g.billId}
                group={g}
                // expanded={isExpanded(g)}
                expanded={true}
                onToggle={() => toggle(g)}
                onChangeAmount={handleChangeAmount}
                onChangeSelect={handleChangeSelect}
                isNotDesktop={isNotDesktop}
              />
            ))}
          </CardList>

          {focusHasMore ? (
            <RevealBtn type="button" $open={showAll} onClick={showAll ? collapseToFocus : revealAllBills}>
              <KeyboardArrowDownRoundedIcon />
              {showAll ? collapseLabel : expandLabel}
            </RevealBtn>
          ) : null}

          {payment_amount > 0 ? (
            <SummaryBar>
              <SummaryItem>
                <div className="label">Open balance</div>
                <div className="num">{formatCurrency(openTotal)}</div>
              </SummaryItem>
              <SummaryItem>
                <div className="label">Payment amount</div>
                <div className="num">{formatCurrency(payment_amount)}</div>
              </SummaryItem>
              <SummaryItem $accent>
                <div className="label">Applied</div>
                <div className="num">{formatCurrency(appliedTotal)}</div>
              </SummaryItem>
              <SummaryItem>
                <div className="label">Remaining</div>
                <div className="num">{formatCurrency(remainingBudget)}</div>
              </SummaryItem>
            </SummaryBar>
          ) : null}
        </>
      ) : (
        <NoItems title="No Open Bills" />
      )}
    </Grid>
  );
};

export default OpenBills;